/**
 * Meeting call shell.
 *
 * Owns the meeting and attendee lifecycle: authentication and guest access, meeting/token
 * loading, attendance recording, waiting-room business rules, message notifications, errors,
 * the leave flow, and cleanup. The Daily client itself is rendered by the WiseFriend call
 * surface (`CustomDailyCallSurface`: Daily call object + Daily React).
 */
import { useState, useCallback, useEffect, useRef } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { AlertCircle, ClipboardList, UserRound, Video } from "lucide-react"
import { Button } from "components/ui/button"
import { Toaster } from "components/ui/toaster"
import { useAuth } from "contexts/AuthContext"
import { continuityRecordPath, requiresMeetingAdmission, type CallLeaveReason, type CallSurfaceHandle, type CallSurfaceProps } from "./call/types"
import { useMeetingEndWatcher, type MeetingEndReason } from "./call/hooks/useMeetingEndWatcher"
import { hasMeetingQuestionCollectionEnded } from "./call/shared/meetingAccess"
import { useMeetingJoinFlow } from "./call/hooks/useMeetingJoinFlow"
import { useMeetingAttendanceRecorder } from "./call/hooks/useMeetingAttendanceRecorder"
import { useMeetingWaitingRoom } from "./call/hooks/useMeetingWaitingRoom"
import { useMessagesNotifications } from "./call/hooks/useMessagesNotifications"
import { BackendAdmittedWebCallSurface } from "./call/custom/BackendAdmittedWebCallSurface"
import { CustomDailyCallSurface } from "./call/custom/CustomDailyCallSurface"
import { NativeCallFallbackGuard } from "mobile/NativeCallFallbackGuard"
import { NativeDailyCallSurface } from "mobile/NativeDailyCallSurface"
import { useNativeCapabilities } from "mobile/useNativeCapabilities"
import { isNativeMobile } from "mobile/nativeBridge"
import { useCallViewport } from "./call/hooks/useCallViewport"
import { refreshNativeViewport } from "mobile/nativeVideoViewport"
import "./call/callViewport.css"
import { getPanelTokenFromUrl } from "./call/shared/panelUrls"

function MeetingCall() {
  const { meetingId } = useParams()
  const { user, isAuthenticated, isAuthLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const native = useNativeCapabilities()
  const viewportRef = useRef<HTMLDivElement>(null)
  const viewportStyle = useCallViewport(viewportRef, native.capabilities?.nativeCall ? refreshNativeViewport : undefined)
  const surfaceRef = useRef<CallSurfaceHandle | null>(null)
  const guestToken = new URLSearchParams(location.search).get("guest")
  const effectiveIsAuthenticated = Boolean(isAuthenticated || user)

  const {
    otherNativeMeeting,
    toolsState, toolsBlocked, retryTools,
    joinInfo,
    setJoinInfo,
    meeting,
    setMeeting,
    guestMeeting,
    loadState,
    setLoadState,
    error,
    setError,
    displayName,
    setDisplayName,
    guestSessionIdRef,
    guestBreakoutTokenRef,
    goToLogin,
    showGuestForm,
    handleGuestJoin,
    handleRetry,
  } = useMeetingJoinFlow({ meetingId, guestToken, isAuthLoading, effectiveIsAuthenticated, userId: user?.id, nativeRecovery: isNativeMobile() })
  const waitRoom = useMeetingWaitingRoom({ meetingId, joinInfo, meeting, setJoinInfo, setMeeting })
  const attendance = useMeetingAttendanceRecorder({ meetingId, joinInfo, meeting, effectiveIsAuthenticated })
  const meetingEventRef = useRef<(event: { type?: string }) => void>(() => undefined)
  const meetingEventListenersRef = useRef(new Set<(event: any) => void>())
  const subscribeMeetingEvents = useCallback((listener: (event: any) => void) => {
    meetingEventListenersRef.current.add(listener)
    return () => {
      meetingEventListenersRef.current.delete(listener)
    }
  }, [])
  const messagesNotifications = useMessagesNotifications({ meetingId, joinInfo, surfaceRef, onMeetingEvent: (event) => meetingEventRef.current(event) })
  const [endReason, setEndReason] = useState<MeetingEndReason | null>(null)
  const { setStatus: setWaitRoomStatus } = waitRoom
  const { reset: resetAttendance } = attendance

  const activeMeeting = meeting || guestMeeting
  const nativeAvailable = Boolean(native.capabilities?.bridgeVersion === 2 && native.capabilities.meetingTools && native.capabilities.nativeCall && joinInfo?.native_calls_enabled && (joinInfo.retained_native || getPanelTokenFromUrl(joinInfo.messages_panel?.url)))

  const renderer = useRef<{ meetingId?: string; native: boolean } | null>(null)
  if (renderer.current?.meetingId !== meetingId) renderer.current = null
  if (!native.pending && joinInfo && !renderer.current) renderer.current = { meetingId, native: nativeAvailable }
  // Capability/flag refreshes must never create a browser peer beside an existing native call.
  const usingNativeSurface = Boolean(renderer.current?.native)
  const backendAdmission = useRef<{ meetingId?: string; enabled: boolean }>({ enabled: false })
  if (backendAdmission.current.meetingId !== meetingId) backendAdmission.current = { meetingId, enabled: false }
  if (joinInfo?.native_calls_enabled && requiresMeetingAdmission(joinInfo)) backendAdmission.current.enabled = true

  const handleMeetingEnded = useCallback((reason: MeetingEndReason) => {
    if (usingNativeSurface) surfaceRef.current?.leave()
    attendance.stopPresenceHeartbeat()
    messagesNotifications.reset()
    setEndReason(reason)
    setLoadState("ended")
  }, [attendance, messagesNotifications, setLoadState, usingNativeSurface])
  const endWatcher = useMeetingEndWatcher({
    meetingId,
    enabled: loadState === "ready",
    // Only an account join can read the meeting. A signed-in user who is not invited joins through
    // the guest link (no `meeting`), and polling would 404 and end their call; guests rely on events.
    canPoll: Boolean(effectiveIsAuthenticated && meeting && !usingNativeSurface),
    scheduledEndTime: activeMeeting?.end_time ?? null,
    onEnded: handleMeetingEnded,
  })
  meetingEventRef.current = (event) => {
    endWatcher.handleMeetingEvent(event)
    meetingEventListenersRef.current.forEach((listener) => listener(event))
  }

  const canReviewQuestions =
    Boolean(meeting?.can_review_questions) ||
    Boolean(user?.is_staff) ||
    Boolean(meeting?.host?.id && user?.id && String(meeting.host.id) === String(user.id))
  const meetingQuestionsClosed = joinInfo?.native_tools_expires_at && Date.parse(joinInfo.native_tools_expires_at) > Date.now() ? false : hasMeetingQuestionCollectionEnded(activeMeeting)
  const showReviewerQuestionPanel = Boolean(meeting?.question_collection_enabled && canReviewQuestions)
  const showGuestQuestionPanel = Boolean(
    guestMeeting?.question_collection_enabled && guestToken && !meetingQuestionsClosed,
  )
  const showParticipantQuestionPanel = Boolean(
    showGuestQuestionPanel ||
      (meeting?.question_collection_enabled && !canReviewQuestions && Boolean(user?.id) && !meetingQuestionsClosed),
  )
  const showQuestionPanel = showReviewerQuestionPanel || showParticipantQuestionPanel

  useEffect(() => {
    // Lock scroll since we render a full-viewport experience
    const previousOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
    return () => {
      document.documentElement.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    resetAttendance()
  }, [guestToken, meetingId, resetAttendance])

  const getLocalParticipantId = useCallback(() => surfaceRef.current?.getLocalParticipantId() || "", [])

  const handleJoined = useCallback((localParticipantId: string) => {
    void attendance.confirmJoin(localParticipantId)
    if (!joinInfo?.retained_native) void attendance.recordAdmission(localParticipantId)
    // The surface emits this only after Daily reports full access, including an
    // admitted knocking-token join that never needed a separate lobby callback.
    attendance.startPresenceHeartbeat(getLocalParticipantId)
  }, [attendance, getLocalParticipantId, joinInfo?.retained_native])

  const handleWaiting = useCallback(() => {
    attendance.stopPresenceHeartbeat()
    setWaitRoomStatus("waiting")
  }, [attendance, setWaitRoomStatus])

  const handleAccessGranted = useCallback((localParticipantId: string) => {
    setWaitRoomStatus("admitted")
    if (!joinInfo?.retained_native) void attendance.recordAdmission(localParticipantId)
    attendance.startPresenceHeartbeat(getLocalParticipantId)
  }, [attendance, getLocalParticipantId, setWaitRoomStatus, joinInfo?.retained_native])

  const handleAccessDenied = useCallback(() => {
    setWaitRoomStatus("idle")
    setLoadState("denied")
  }, [setLoadState, setWaitRoomStatus])

  const handleParticipantCount = useCallback(() => {
    // The surface shows the count in its own header; the shell has nothing to do with it.
  }, [])

  const handleSurfaceError = useCallback((message: string) => {
    setError(message)
    setLoadState("error")
  }, [setError, setLoadState])

  const closeOrGoHome = useCallback(() => {
    window.close()
    if (!window.closed) {
      navigate("/home")
    }
  }, [navigate])

  const handleLeft = useCallback((reason?: CallLeaveReason) => {
    attendance.stopPresenceHeartbeat()
    messagesNotifications.reset()
    if (reason === "removed" || reason === "access-ended") {
      setLoadState(reason)
      return
    }
    if (reason === "ended" || reason === "cancelled") {
      setEndReason(reason)
      setLoadState("ended")
      return
    }
    // A mentor or mentee is shown where their For next time notes will appear before the tab closes.
    if (joinInfo?.continuity) {
      setLoadState("left")
      return
    }
    closeOrGoHome()
  }, [attendance, closeOrGoHome, joinInfo?.continuity, messagesNotifications, setLoadState])

  const continuityLink = joinInfo?.continuity
  const continuityMessage = continuityLink?.role === "mentor" && !continuityLink.relationship_id
    ? "For next time notes for each of your mentees are being prepared from this session. They usually arrive within a minute."
    : "Your For next time notes from this session are being prepared. They usually arrive within a minute, and the topics you already had are there now."

  const messageToast = messagesNotifications.toast
  const surfaceProps: CallSurfaceProps | null = joinInfo && meetingId ? {
    meetingId,
    joinInfo,
    onJoinInfoRefresh: setJoinInfo,
    meeting,
    activeMeeting,
    attendee: {
      userId: user?.id,
      isAuthenticated: effectiveIsAuthenticated,
      isGuest: joinInfo.retained_native ? Boolean(joinInfo.retained_native_guest) : Boolean(guestToken && !meeting),
      guestToken,
      displayName: displayName || joinInfo.display_name || "",
      guestSessionId: guestSessionIdRef.current,
      breakoutToken: joinInfo?.breakout_token || guestBreakoutTokenRef.current,
    },
    toolsRecovery: joinInfo.retained_native ? { status: toolsState, blocked: toolsBlocked, retry: retryTools } : undefined,
    waitRoom: {
      status: waitRoom.status,
      unlocked: waitRoom.unlocked,
      unlocking: waitRoom.unlocking,
      unlockError: waitRoom.unlockError,
      canUnlock: waitRoom.canUnlock,
      unlock: waitRoom.unlock,
    },
    questions: {
      showPanel: showQuestionPanel,
      showReviewerPanel: showReviewerQuestionPanel,
      showGuestPanel: showGuestQuestionPanel,
      showParticipantPanel: showParticipantQuestionPanel,
      canReview: canReviewQuestions,
    },
    messages: {
      enabled: messagesNotifications.enabled,
      unreadCount: messagesNotifications.unreadCount,
      notification: messageToast ? {
        toast: messageToast,
        onOpen: () => messagesNotifications.openMessages({
          threadId: messageToast.threadId,
          recipientIdentityKey: messageToast.recipientIdentityKey,
        }),
        onDismiss: messagesNotifications.dismissToast,
      } : null,
    },
    updateMeeting: setMeeting,
    subscribeMeetingEvents,
    onJoined: handleJoined,
    onWaiting: handleWaiting,
    onAccessGranted: handleAccessGranted,
    onAccessDenied: handleAccessDenied,
    onParticipantCount: handleParticipantCount,
    onMessagesSurfaceOpenChange: messagesNotifications.setSurfaceOpen,
    onDirectMessageSignal: messagesNotifications.notifyDirectMessageEvent,
    onError: handleSurfaceError,
    onLeft: handleLeft,
  } : null

  const webSurface = surfaceProps ? (backendAdmission.current.enabled
    ? <BackendAdmittedWebCallSurface key={meetingId} ref={surfaceRef} {...surfaceProps} />
    : <CustomDailyCallSurface ref={surfaceRef} {...surfaceProps} />) : null

  return (
    <div ref={viewportRef} data-testid="meeting-call-viewport" style={viewportStyle} className="meeting-call-viewport fixed inset-0 z-[100] flex flex-col overflow-hidden bg-black">
      <div className="call-notification-region"><Toaster placement="inline" /></div>
      <div data-testid="meeting-call-content" className="relative min-h-0 flex-1 overflow-auto">
      {loadState === "loading" && (
        <div className="flex items-center justify-center w-full h-full">
          <div className="h-24 w-24 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        </div>
      )}

      {(loadState === "guest-choice" || loadState === "guest-form") && (
        <div className="flex h-full w-full items-center justify-center overflow-y-auto bg-[#0f1419] px-5 py-8" style={{ backgroundImage: "radial-gradient(60rem 30rem at 50% -10%, rgba(246,163,92,0.22), transparent 70%)" }}>
          <div data-testid="guest-entry-card" className="w-full max-w-md rounded-3xl bg-[#151d26] p-7 text-white shadow-2xl ring-1 ring-white/10 sm:p-9">
            <div className="mb-7 flex flex-col items-center text-center">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f6a35c]/15 text-[#f6a35c] ring-1 ring-[#f6a35c]/30">
                <Video className="h-7 w-7" />
              </span>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">WiseFriend meeting</p>
              <h1 className="mt-2 text-2xl font-semibold leading-tight">{loadState === "guest-form" ? "Join as a guest" : "Join meeting"}</h1>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/65">
                {loadState === "guest-form"
                  ? "Enter the name everyone will see in the call."
                  : error || "You have a link to this meeting. Choose how you want to enter the call."}
              </p>
            </div>
            {loadState === "guest-choice" ? (
              <div className="space-y-3">
                <button type="button" onClick={showGuestForm} className="flex h-12 w-full items-center justify-center rounded-full bg-[#f6a35c] text-base font-semibold text-[#1c2530] transition-colors hover:bg-[#f8b478] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
                  Join as guest
                </button>
                <button
                  type="button"
                  onClick={goToLogin}
                  className="flex h-12 w-full items-center justify-center rounded-full bg-white/10 text-base font-semibold text-white transition-colors hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                >
                  {effectiveIsAuthenticated ? "Log in with a different account" : "Log in"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleGuestJoin} className="space-y-4">
                <div>
                  <label htmlFor="guest-display-name" className="mb-1.5 block text-sm font-medium text-white/80">
                    Display name
                  </label>
                  <div className="relative">
                    <UserRound className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
                    <input
                      id="guest-display-name"
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                      placeholder="Your name"
                      maxLength={80}
                      autoComplete="name"
                      autoFocus
                      className="h-12 w-full rounded-full border border-white/15 bg-white/5 pl-11 pr-4 text-base text-white placeholder:text-white/40 focus:border-[#f6a35c] focus:outline-none focus:ring-2 focus:ring-[#f6a35c]/40"
                    />
                  </div>
                </div>
                <button type="submit" className="flex h-12 w-full items-center justify-center rounded-full bg-[#f6a35c] text-base font-semibold text-[#1c2530] transition-colors hover:bg-[#f8b478] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
                  Join meeting
                </button>
                <button
                  type="button"
                  onClick={goToLogin}
                  className="flex h-12 w-full items-center justify-center rounded-full bg-white/10 text-base font-semibold text-white transition-colors hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                >
                  {effectiveIsAuthenticated ? "Log in with a different account" : "Log in instead"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {loadState === "joining" && (
        <div className="flex items-center justify-center w-full h-full">
          <div className="h-24 w-24 rounded-full border-4 border-white/30 border-t-white animate-spin" />
        </div>
      )}

      {loadState === "error" && (
        <div className="flex flex-col items-center justify-center w-full h-full text-center text-white px-6">
          <AlertCircle className="h-8 w-8 mb-3 text-white" />
          <p className="text-lg font-semibold mb-2">Unable to start the meeting</p>
          {error && <p className="text-sm text-white/80 mb-4 max-w-xl">{error}</p>}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button onClick={handleRetry}>Try again</Button>
            {otherNativeMeeting && <Button onClick={() => navigate(`/meetings/${otherNativeMeeting}/call`)}>Return to active call</Button>}
            <Button variant="secondary" onClick={() => navigate("/home")}>
              Go to dashboard
            </Button>
          </div>
        </div>
      )}

      {loadState === "ended" && (
        <div className="flex flex-col items-center justify-center w-full h-full text-center text-white px-6">
          <AlertCircle className="h-8 w-8 mb-3 text-white" />
          <p className="text-lg font-semibold mb-2">{endReason === "cancelled" ? "This meeting was cancelled" : "This meeting has ended"}</p>
          <p className="text-sm text-white/80 mb-4 max-w-xl">{endReason === "cancelled" ? "This meeting is no longer available to join." : "The meeting is over for everyone."}</p>
          <Button variant="secondary" onClick={() => navigate("/home")}>
            Go to dashboard
          </Button>
        </div>
      )}

      {loadState === "left" && continuityLink && (
        <div data-testid="post-call-continuity" className="flex flex-col items-center justify-center w-full h-full text-center text-white px-6">
          <ClipboardList className="h-8 w-8 mb-3 text-white" />
          <p className="text-lg font-semibold mb-2">Thanks for joining</p>
          <p className="text-sm text-white/80 mb-4 max-w-xl">{continuityMessage}</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button onClick={() => navigate(continuityRecordPath(continuityLink))}>See your For next time notes</Button>
            <Button variant="secondary" onClick={closeOrGoHome}>
              Close
            </Button>
          </div>
        </div>
      )}

      {loadState === "access-ended" && (
        <div className="flex flex-col items-center justify-center w-full h-full text-center text-white px-6">
          <AlertCircle className="h-8 w-8 mb-3 text-white" />
          <p className="text-lg font-semibold mb-2">Meeting access has ended</p>
          <p className="text-sm text-white/80 mb-4 max-w-xl">Your call has stopped because meeting access is no longer available.</p>
          <Button variant="secondary" onClick={() => navigate("/home")}>Go to dashboard</Button>
        </div>
      )}

      {loadState === "removed" && (
        <div className="flex flex-col items-center justify-center w-full h-full text-center text-white px-6">
          <AlertCircle className="h-8 w-8 mb-3 text-white" />
          <p className="text-lg font-semibold mb-2">The host removed you from this meeting</p>
          <p className="text-sm text-white/80 mb-4 max-w-xl">You can return to your dashboard, or rejoin if the host invites you back.</p>
          <Button variant="secondary" onClick={() => navigate("/home")}>
            Go to dashboard
          </Button>
        </div>
      )}

      {loadState === "denied" && (
        <div className="flex flex-col items-center justify-center w-full h-full text-center text-white px-6">
          <AlertCircle className="h-8 w-8 mb-3 text-white" />
          <p className="text-lg font-semibold mb-2">The host did not admit you</p>
          <p className="text-sm text-white/80 mb-4 max-w-xl">You can try again later or return to your dashboard.</p>
          <Button variant="secondary" onClick={() => navigate("/home")}>
            Go to dashboard
          </Button>
        </div>
      )}

      {loadState === "ready" && surfaceProps && (
        <div className="flex w-full h-full flex-col bg-black" data-testid="meeting-call-surface">
          {!native.pending && native.capabilities?.nativeCall && !usingNativeSurface && <p role="status" className="shrink-0 bg-slate-800 px-3 py-2 text-sm text-white">{native.capabilities.bridgeVersion < 2 ? 'Update the app to use native calling and picture-in-picture. This call uses the web version.' : 'Native calling is not enabled for this meeting. This call uses the web version.'}</p>}
          <div className="min-h-0 flex-1">
          {native.pending ? <div role="status" className="flex h-full items-center justify-center text-white">Preparing your call…</div>
            : usingNativeSurface
              ? <NativeDailyCallSurface key={meetingId} ref={surfaceRef} {...surfaceProps} />
              : isNativeMobile() ? <NativeCallFallbackGuard>{webSurface}</NativeCallFallbackGuard> : webSurface}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default MeetingCall
